import { Skeleton, Space } from 'antd';
import { OfferCardMessage, OfferCardMessageData } from 'components/Dialogue';
import BubbleMessage from 'components/Dialogue/components/Message/BubbleMessage';
import { LocalMessage } from 'hooks/business/conversation';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { itemCardProduct, ItemCardProductParams, itemSkuGet } from 'services/api/conversation';
import chatMsgRenderFailReport from 'components/Dialogue/components/msgReport';
import { cloneDeep } from 'lodash-es';
import { offer, OfferParams } from 'services/api/offer';
import CardMessage from 'components/Dialogue/components/Message/CardMessage';
import styled from '@emotion/styled';
import { useQuery, QueryStatus } from '@tanstack/react-query';
import { ApiQueryKey } from 'services/config/queryKey';
import { useInViewport } from 'ahooks';
import { DialogueModeContext } from '../DialogueModeContext.tsx';
import { formatNumber } from 'utils/numberFormat.ts';

const SkeletonWrapper = styled.div`
  margin: -8px;
  > div {
    display: flex;
    padding: 8px;
    > section {
      flex: 1 0 0;
      margin-left: 12px;
    }
  }
  > footer {
    display: flex;
    justify-content: flex-end;
    padding: 8px;
    font-size: 0;
    border-top: 1px dashed var(--fett-border-default);
  }
  .ant-skeleton-button-sm {
    height: 16px;
  }
`;

export interface LazyOfferMessageProps {
  storeId: Conversation.Conversation['storeId'];
  msgId: string;
  externalMsgId: string;
  externalItemId: Conversation.MsgItem['externalItemId'];
  externalStoreId: string;
  msgContent: Conversation.MsgOffer;
  rawMsg: LocalMessage;
  children?: (component: React.ReactNode, status: QueryStatus) => React.ReactNode;
  isBuyerMessage: boolean;
  conversationId: string;
  conversation: Conversation.Conversation;
  quote?: React.ReactNode;
  lazyScrollContainer?: Element | null;
  className?: HTMLDivElement['className'];
}

const LazyOfferMessage = (props: LazyOfferMessageProps) => {
  const {
    externalItemId,
    storeId,
    msgId,
    externalMsgId,
    children,
    isBuyerMessage,
    msgContent,
    rawMsg,
    quote,
    externalStoreId,
    conversation,
    lazyScrollContainer,
    className,
  } = props;
  const [content, setContent] = useState<OfferCardMessageData>();
  const { t } = useTranslate();

  const isFaq = useContext(DialogueModeContext) === 'faq';

  const ref = useRef<HTMLDivElement>(null);
  const [inViewPort = false] = useInViewport(ref, {
    root: lazyScrollContainer,
  });

  const itemQueryParams = useMemo(
    () => ({
      storeId,
      externalItemId,
      needStockRule: true,
    }),
    [storeId, externalItemId],
  );
  const itemQuery = useQuery({
    enabled: !!conversation && (inViewPort || isFaq),
    queryKey: [ApiQueryKey.ItemCardProduct, itemQueryParams],
    queryFn: ({ queryKey, signal }) =>
      itemCardProduct(queryKey[1] as ItemCardProductParams, signal),
    staleTime: 3 * 60 * 1000,
  });

  const offerQueryParams = useMemo(
    () => ({
      storeId,
      messageId: externalMsgId,
      offerId: msgContent.offerId,
    }),
    [storeId, externalMsgId, msgContent.offerId],
  );
  const offerQuery = useQuery({
    enabled: !!conversation && inViewPort,
    queryKey: [ApiQueryKey.OfferCard, offerQueryParams],
    queryFn: ({ queryKey, signal }) => offer(queryKey[1] as OfferParams, signal),
    staleTime: 3 * 60 * 1000,
  });

  const modelId = offerQuery.data?.modelId;
  const itemSkuQuery = useQuery({
    enabled: !!modelId,
    queryKey: [ApiQueryKey.OfferCard, modelId],
    queryFn: ({ queryKey, signal }) => itemSkuGet(queryKey[1] as string, signal),
    staleTime: 3 * 60 * 1000,
    retry: 2,
    retryDelay: 600,
  });

  useEffect(() => {
    const product = itemQuery.data;
    const offer = offerQuery.data;
    if (offer && product) {
      const {
        itemName: title,
        itemUrl: link,
        imageUrl: [img],
        unit,
        skuLowPrice,
        skuHighPrice,
        originalHighPrice,
        itemSku,
      } = product;
      const priceNumber =
        conversation.channel === 'LAZADA' && skuHighPrice === 0 ? originalHighPrice : skuHighPrice;

      const price =
        skuLowPrice === skuHighPrice
          ? `${unit} ${formatNumber(priceNumber, '0,0.00')}`
          : `${unit} ${formatNumber(skuLowPrice, '0,0.00')} - ${unit} ${formatNumber(
              skuHighPrice,
              '0,0.00',
            )}`;

      const originPrice =
        originalHighPrice > priceNumber
          ? `${unit} ${formatNumber(originalHighPrice, '0,0.00')}`
          : null;

      const discount =
        originalHighPrice > priceNumber
          ? `${formatNumber((priceNumber - originalHighPrice) / originalHighPrice, '0.00%')}`
          : null;
      const offerData: OfferCardMessageData = {
        id: externalItemId,
        title,
        price,
        link,
        img,
        originPrice,
        discount,
        unit,
        itemSku,
        externalStoreId,
        offer,
        spuStockStatus: 'InStock',
      };
      setContent(offerData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemQuery.data, offerQuery.data]);

  useEffect(() => {
    const skuInfo = itemSkuQuery.data;
    if (skuInfo) {
      const { skuName, sellerSku, price } = skuInfo;
      setContent((prev) => {
        if (prev) {
          return {
            ...prev,
            skuName,
            sellerSku,
            price: price ? `${prev.unit} ${formatNumber(skuInfo.price, '0,0.00')}` : prev.price,
          };
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemSkuQuery.data]);

  useEffect(() => {
    if (itemQuery.isError || offerQuery.isError) {
      chatMsgRenderFailReport('offer', cloneDeep(rawMsg));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemQuery.isError, offerQuery.isError]);

  const status = useMemo(() => {
    if (itemQuery.status === 'loading' || offerQuery.status === 'loading') {
      return 'loading';
    }
    if (itemQuery.status === 'success' && offerQuery.status === 'success') {
      return 'success';
    }
    return 'error';
  }, [itemQuery.status, offerQuery.status]);

  const component: React.ReactNode = useMemo(() => {
    if (status === 'loading') {
      return (
        <CardMessage>
          <SkeletonWrapper>
            <div>
              <Skeleton.Avatar active shape="square" size="large" />
              <section>
                <Skeleton.Button active block size="small" />
                <Space>
                  <Skeleton.Button active size="small" />
                  <Skeleton.Button block active size="small" />
                </Space>
              </section>
            </div>
            <footer>
              <Skeleton.Button active size="small" />
            </footer>
          </SkeletonWrapper>
        </CardMessage>
      );
    }
    if (status === 'success' && content) {
      return (
        <OfferCardMessage
          storeId={storeId}
          msgId={msgId}
          externalMsgId={externalMsgId}
          msgContent={msgContent}
          dataSource={content}
          isBuyerMessage={isBuyerMessage}
          quote={quote}
          lazyScrollContainer={lazyScrollContainer}
        />
      );
    }
    return (
      <BubbleMessage>
        {t('scene.chatWorkspace.messageThread.lazyCard.error', { type: 'Offer' })}
      </BubbleMessage>
    );
  }, [
    msgContent,
    content,
    status,
    storeId,
    msgId,
    externalMsgId,
    isBuyerMessage,
    quote,
    lazyScrollContainer,
    t,
  ]);
  return (
    <div className={className} ref={ref}>
      {typeof children === 'function' ? children(component, status) : component}
    </div>
  );
};
export default LazyOfferMessage;
